// BP 56 — frontend i18n entry point. `main.tsx` imports this module for its
// SIDE EFFECT only (`import './i18n';`), the same idiom as
// `stores/themeStore.ts` — module load synchronously initializes i18next with
// the bundled `en` catalogs (so first paint is always correct English), then
// asynchronously negotiates + applies the resolved locale per §6:
//
//   1. `user.locale` (authenticated, non-null)     — handled in authStore.ts's
//                                                      setAuth() (WP-0A's
//                                                      login-sync hook), not
//                                                      here — it only fires
//                                                      once a login response
//                                                      exists.
//   2. `localStorage['worksnip.locale']` (explicit) — this module, below.
//   3. `navigator.languages` base-tag match          — this module, below.
//   4. `en`                                          — the persisted default.
//
// Gated behind `isI18nSwitcherEnabled()` (BP 56 §12 — ships dark): while the
// gate is off the app is hard-pinned to `en` regardless of storage/browser
// language, so a non-English visitor never sees a half-translated UI before
// the platform switch flips on. See gate.ts for why this can't yet read the
// real `Setting('i18n_ui_enabled')`.

import { initI18n, applyLocale, i18n, getActiveLocale, PSEUDO_LOCALE, PSEUDO_ENABLED } from './runtime';
import { useLocale, useActiveLocale } from './localeStore';
import { negotiateFromNavigator } from './negotiate';
import { isI18nSwitcherEnabled } from './gate';
import { useAuth } from '../stores/authStore';
import { SUPPORTED_UI_LOCALES, DEFAULT_LOCALE, LOCALE_NATIVE_NAMES, isSupportedUiLocale, type UiLocale } from './constants';

export {
  initI18n, applyLocale, i18n, getActiveLocale,
  useLocale, useActiveLocale,
  negotiateFromNavigator,
  isI18nSwitcherEnabled,
  SUPPORTED_UI_LOCALES, DEFAULT_LOCALE, LOCALE_NATIVE_NAMES, isSupportedUiLocale,
  PSEUDO_LOCALE, PSEUDO_ENABLED,
};
export type { UiLocale };

// Kick off i18next init immediately (synchronous-feeling: `en` resources are
// already in memory, no network/dynamic import involved).
void initI18n();

if (typeof document !== 'undefined') {
  // Read the persisted auth user (zustand-persist hydrates synchronously from
  // localStorage on store creation) so a RETURNING logged-in user whose org
  // has `i18n_ui_enabled` ON honors their saved locale on a plain page refresh
  // — login-sync only re-fires on a fresh setAuth(), not on rehydration.
  const enabled = isI18nSwitcherEnabled(useAuth.getState().user);
  const st = useLocale.getState();
  if (!enabled) {
    // Dark: force `en` regardless of what negotiation/storage would otherwise
    // pick, without touching the persisted `explicit` flag (so flipping the
    // gate on later resumes the user's real preference instead of losing it).
    if (st.locale !== DEFAULT_LOCALE) void applyLocale(DEFAULT_LOCALE);
    else void applyLocale(st.locale);
  } else if (st.explicit) {
    // Step 2 — an explicit prior choice (anon or pre-login) wins; just apply
    // what's already persisted.
    void applyLocale(st.locale);
  } else {
    // Step 3/4 — no explicit choice on file yet: negotiate from the browser.
    // Not marked explicit, so a later `user.locale` from login (or a future
    // browser-language change) can still override it.
    const negotiated = negotiateFromNavigator();
    if (negotiated !== st.locale) useLocale.getState().setLocale(negotiated, { explicit: false, skipPatch: true });
    else void applyLocale(st.locale);
  }
}
